import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';

import {MvsCoreService} from '../../../../../../../core/shared/service/mvs-core.service';
import {MvsCrudService} from '../../../../../../../core/shared/service/crud/mvs-crud.service';
import {FilterCriteria} from '../../../../../../../core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from '../../../../../../../core/shared/dto/object/request/object-request-list';
import {ObjectRequestComplex} from '../../../../../../../core/shared/dto/object/request/object-request-complex';
import {
    ObjectRequestComplexNode
} from '../../../../../../../core/shared/dto/object/request/object-request-complex-node';

import {CwCasePersonDto} from '../../../../../model/dto/entity/cw-case-person.dto';
import {
    CW_CASE_PERSON_CHECK_NOT_VERIFIABLE_MESSAGE,
    checkCaseCapturedIdentities,
    CwCasePersonCheckResult,
    CwCasePersonCustomerMatrixOutcome,
} from './cw-case-person-check';
import {unlinkedCasePersonSuggestions} from './cw-case-person-suggestion';

import {CrPersonSuggestion} from '../../../../../../cr/logic/cr-person-suggestion';
import {CrPersonMatchResult} from '../../../../../../cr/logic/cr-person-match';
import {CrPersonResolution} from '../../../../../../cr/logic/cr-person-discrepancy';
import {CrPersonMatrixService} from '../../../../../../cr/service/api/cr-person-matrix.service';
import {CustomerPersonMatrixDto} from '../../../../../../cr/model/dto/api/customer-person-matrix.dto';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: Check
 * Reason: The two person questions of a case, asked SEPARATELY and labelled apart — J1 "ist der
 *         erfasste Stand der bereits verknüpften Person noch aktuell?" as a read-only list, J2 "wer
 *         ist dieser Mensch?" by handing the unlinked rows to cr-customer-persons as a delivery.
 *
 * @ticket TM-1000001991
 */

/**
 * [TM-1000001991] What the host learns about the J1 findings — see
 * {@link CwCasePersonCheckComponent.onCheckSummary}.
 *
 * <p>Every number here is counted from the J1 result list and from nothing else. In particular there is
 * no count derived from `cr-customer-persons`' match results: that event is diagnostic, fires on at
 * least five paths, and a counter fed from it would drift (see
 * {@link CwCasePersonCheckComponent.handleMatchResultChange}).
 *
 * @ticket TM-1000001991
 */
export interface CwCasePersonCheckSummary {

    /** The case the findings belong to — so a late-arriving emission can be attributed. */
    caseId: number | null;

    /** BOUND rows examined, i.e. the length of {@link results}. Unlinked rows are not J1's business. */
    checked: number;

    /** Rows whose captured snapshot still agrees with the linked person's current name. */
    matching: number;

    /** Rows whose captured snapshot DISAGREES — the finding a processor has to act on. */
    diverging: number;

    /** Rows that carry no captured snapshot at all. An empty snapshot is not a wrong one. */
    withoutCapturedIdentity: number;

    /**
     * [S16] Bound rows whose linked `pm.Person` is NOT among the customer's people.
     *
     * <p>ORTHOGONAL to the three counts above and therefore NOT a subset of any of them: such a row is
     * additionally counted as `matching`, `diverging` or `withoutCapturedIdentity` according to its
     * NAME. Summing the four would double-count on exactly the rows that carry both problems.
     *
     * <p>`0` while {@link customerBindingCheck} is `'skipped'` or `'failed'` — in neither state is the
     * question answered, and a zero there means „not asked", not „none".
     */
    notBoundToCustomer: number;

    /**
     * [S16 / AUFLAGE A6] Whether the binding question was answered at all, and how it went.
     *
     * <p>`'skipped'` — no customer, so the question does not apply.
     * <br>`'loaded'` — the customer's person matrix was read; {@link notBoundToCustomer} is meaningful.
     * <br>`'failed'` — the read failed; every bound row carries `NOT_VERIFIABLE` and nothing is known.
     *
     * <p>The three are kept apart for the same reason `CwCasePersonCustomerMatrixOutcome` is a tagged
     * union: a host that could not tell a failed read from „nothing to report" would present an
     * infrastructure hiccup as a clean bill of health.
     */
    customerBindingCheck: 'skipped' | 'loaded' | 'failed';

    /** The findings themselves, in the order the rows arrived. */
    results: CwCasePersonCheckResult[];
}

/**
 * [TM-1000001991] The two person questions of a case, on one surface, and never blurred into one.
 *
 * <h3>AUFLAGE A3 — the labelling IS the feature</h3>
 * Two entirely different questions are asked about the persons of one case, and three previous
 * versions of this feature answered them with one mechanism and therefore answered at least one of
 * them wrongly:
 *
 * <table>
 *   <tr><th></th><th>Section J1 „Aktualität"</th><th>Section J2 „Zuordnung"</th></tr>
 *   <tr><td>Rows</td><td>BOUND (`personDtoId` set), ~1.068</td><td>UNLINKED, ~139</td></tr>
 *   <tr><td>Question</td><td>„Diese Person kennen wir — ist ihr im Vorgang erfasster Stand noch
 *       aktuell?"</td><td>„Wer ist dieser Mensch?"</td></tr>
 *   <tr><td>Mechanism</td><td>a pair comparison of a pair that is not in doubt</td><td>a SEARCH over
 *       the customer's people</td></tr>
 *   <tr><td>Owner</td><td>`checkCaseCapturedIdentities`</td><td>`cr-customer-persons` +
 *       `CrPersonMatch`</td></tr>
 * </table>
 *
 * <p><b>A J1 finding must never be readable as a J2 „keine Übereinstimmung".</b> That is the mistake
 * this feature has made three times: running a bound row through the matcher makes it report „diese
 * Person kennen wir nicht" for a person who is right there, merely under a stale captured name. The
 * defence is not a comment — it is the two headings on screen ({@link SECTION_CURRENCY_TITLE},
 * {@link SECTION_ASSIGNMENT_TITLE}), each carrying its own question verbatim in the template, plus the
 * fact that no row ever appears in both sections: the two row sets are disjoint by construction
 * (`typeof personDtoId === 'number'` selects one, its negation the other).
 *
 * <h3>[S16 / AUFLAGE A6] J1 asks a SECOND question, and keeps it apart from the first</h3>
 * Besides „ist der erfasste Stand noch aktuell?", J1 answers „hängt die verknüpfte Person überhaupt an
 * diesem Kunden?" — measured on dev/tenantA, 95 of 1.068 bound rows do not. The two are ORTHOGONAL and
 * are rendered in two SEPARATE columns, because a row can be „Stand aktuell" AND „Nicht zugeordnet" at
 * the same time and neither statement may displace the other. Folding the binding into the status tag
 * would repeat, inside J1, exactly the conflation AUFLAGE A3 keeps J1 and J2 apart for.
 *
 * <p>The answer is read here — {@link loadCustomerMatrix}, `getMatrix` only, `setCustomerId` NEVER —
 * and handed to `checkCaseCapturedIdentities` as a `CwCasePersonCustomerMatrixOutcome`, so the logic
 * file stays pure. A failed read is SHOWN (`NOT_VERIFIABLE` on every bound row, plus a section notice
 * with a retry), never swallowed.
 *
 * <h3>Read-only, on both halves</h3>
 * J1 displays; it never refreshes a snapshot (that would change what the BiPRO/TAA wire sends — a
 * deliberately deferred extension), and the binding verdict is an ANNOTATION, not a gate: this component
 * writes NOTHING to `cw.CwCasePerson` and holds no matcher of its own.
 *
 * <p>J2's writes belong to `cr-customer-persons`, which owns them end to end. This component only hands
 * that surface a delivery and listens.
 *
 * <h3>The bindings on `cr-customer-persons` are prescribed, not chosen</h3>
 * <ul>
 *   <li><b>`[allowCreate]="false"` is MANDATORY.</b> With creation enabled the create dialog renders a
 *       hidden `CrPersonMatrixComponent` which calls `setCustomerId` on the root singleton and pins it
 *       to this customer for the rest of the browser session. There is no case in which this surface
 *       needs to create a person; the processor does that in the CRM.</li>
 *   <li><b>`[selectionMode]` is deliberately NOT bound</b> — its default `'none'` is what this surface
 *       wants. J2 shows and reconciles; it does not pick.</li>
 *   <li><b>`[readonly]` is passed through.</b> ⚠ At `readonly === true` `onDiscrepancyResolved` can
 *       never fire (`cr-customer-persons.canApplyResolutions()` requires `!readonly`). Nothing in this
 *       component waits for it: {@link handleDiscrepancyResolved} is a pure recorder, and no state,
 *       count or rendering depends on it having arrived.</li>
 * </ul>
 *
 * <h3>Guard 1 — the embedded surface is DESTROYED and rebuilt on a case or customer switch</h3>
 * Re-binding `[customerId]` on a live instance is not enough: the embedded surface reaches shared,
 * session-scoped state, and a stale instance re-pointed at another customer is exactly the class of
 * defect this feature keeps producing. So the surface is torn down and a NEW one is created.
 *
 * <p>The mechanism is {@link embeddedSurfaceKeys} — a one-element array whose value CHANGES, rendered by
 * an `@for` with `track surfaceKey`. A changed track key makes Angular destroy the old view and create a
 * fresh one, deterministically and within a single change-detection pass. A naked `@if` flip cannot
 * promise that: when `casePersons` arrives through the `@Input` there is no asynchronous gap between
 * „false" and „true", so the template would never observe the false state and the instance would survive.
 *
 * <h3>Guard 4 — without a customer, section J2 does not exist</h3>
 * `@if (customerId)` wraps the WHOLE J2 section, heading and counters included, not merely the embedded
 * surface. A heading that poses the question „wer ist dieser Mensch?" over a case with no customer poses
 * a question nothing on screen could answer — and an empty area under a live heading reads as „nichts
 * gefunden", which is the A3 confusion again in a different costume. The `@else` branch says in one
 * German sentence that the section is not shown and why; it carries neither the heading nor a count, and
 * no request is issued in that state.
 *
 * <h3>Guard 2 — `onMatchResultChange` is DIAGNOSTIC</h3>
 * See {@link handleMatchResultChange}. It never produces suggestions and never advances a counter.
 *
 * <h3>Guard 3 — no write branch, and no „X neue Personen"</h3>
 * `CrPersonMatchResult.level === 'new'` means „not found among this customer's people", which is a
 * statement about the STOCK and only as good as the delivery that produced it. A J2 delivery carries a
 * NAME and nothing else (2 of 19 snapshot columns reach the frontend DTO), so „new" here routinely means
 * „we delivered too little to recognise her", not „she does not exist". Counting it as new persons would
 * put a number on screen that invites exactly the duplicate creation this whole feature prevents.
 *
 * @ticket TM-1000001991
 */
@Component({
    selector: 'cw-case-person-check',
    templateUrl: './cw-case-person-check.component.html',
    styleUrls: ['./cw-case-person-check.component.scss'],
    standalone: false
})
export class CwCasePersonCheckComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * [AUFLAGE A3] The heading of section J1. Held as a constant, not only as template text, so the
     * separation the auflage demands is pinned by a spec rather than by review.
     *
     * <p>It names the row set („bereits verknüpfte Personen") and the question („Aktualität") in one
     * line, because a reader who sees only „Prüfung" cannot tell the two sections apart.
     */
    static readonly SECTION_CURRENCY_TITLE = 'Aktualität — bereits verknüpfte Personen';

    /** [AUFLAGE A3] The question J1 answers, rendered verbatim under its heading. */
    static readonly SECTION_CURRENCY_QUESTION =
        'Diese Personen kennen wir bereits. Geprüft wird nur, ob der im Vorgang erfasste Stand noch zur '
        + 'verknüpften Person passt.';

    /**
     * [AUFLAGE A5] What the currency check actually compares — on screen, not only in a doc comment.
     *
     * <p>The frontend `CwCasePersonDto` carries 2 of the 19 deprecated snapshot columns, so J1 is a
     * NAMES check and nothing more. Without this line „Stand aktuell" reads as „alle Angaben stimmen",
     * which is a claim about a birth date nobody compared.
     */
    static readonly SECTION_CURRENCY_SCOPE_NOTE =
        'Verglichen werden Vor- und Nachname; Geburtsdatum und weitere Angaben sind nicht Teil dieser '
        + 'Prüfung.';

    /** [AUFLAGE A3] The heading of section J2 — the OTHER row set and the OTHER question. */
    static readonly SECTION_ASSIGNMENT_TITLE = 'Zuordnung — noch nicht verknüpfte Personen';

    /** [AUFLAGE A3] The question J2 answers, rendered verbatim under its heading. */
    static readonly SECTION_ASSIGNMENT_QUESTION =
        'Diese Personen sind mit keiner Person im System verknüpft. Hier wird gesucht, wer dieser '
        + 'Mensch ist.';

    /**
     * [AUFLAGE A3] The sentence that stops a J1 finding from being read as a J2 one. Shown in section
     * J1 whenever it has findings, because that is the moment the confusion is possible at all.
     */
    static readonly SECTION_CURRENCY_DELIMITATION =
        'Ein Befund in diesem Abschnitt bedeutet NICHT, dass die Person unbekannt wäre — die '
        + 'Verknüpfung steht, nur der erfasste Stand weicht ab.';

    /** The heading the embedded `cr-customer-persons` carries inside section J2. */
    static readonly ASSIGNMENT_EMBED_HEADER = 'Personen des Kunden';

    /** German wording for a failed self-load — an empty list and a failed read mean opposite things. */
    static readonly LOAD_FAILED_MESSAGE =
        'Die Personen des Vorgangs konnten nicht geladen werden. Die Prüfung ist deshalb unvollständig.';

    /** [S16] Column heading of the customer-binding verdict — shown only when the question was asked. */
    static readonly BINDING_COLUMN_TITLE = 'Zuordnung zum Kunden';

    /** [S16] What a row says when its linked person IS among the customer's people. */
    static readonly BINDING_BOUND_LABEL = 'Zugeordnet';

    /** [S16] Short label of `NOT_BOUND_TO_CUSTOMER`; the full sentence is the logic file's constant. */
    static readonly BINDING_NOT_BOUND_LABEL = 'Nicht zugeordnet';

    /** [AUFLAGE A6] Short label of `NOT_VERIFIABLE`; the full sentence is the logic file's constant. */
    static readonly BINDING_NOT_VERIFIABLE_LABEL = 'Nicht prüfbar';

    /** The case whose persons are examined. Also the filter of the self-load. */
    @Input() caseId: number;

    /**
     * The case's customer — the SECURITY ANCHOR and the stock section J2 searches in.
     *
     * <p>`null`/`undefined` renders no J2 section at all and issues no request (guard 4). J1 is
     * unaffected: a currency check needs no customer.
     */
    @Input() customerId: number | null = null;

    /**
     * The case persons, when the host already holds them.
     *
     * <p>`undefined` — the input never bound — switches on the self-load in {@link loadCasePersons},
     * which reads with the SAME complex node as `cw-case-object-step-persons.loadCasePersons()`
     * (`person` → `personDetail`, plus `contractPersonType`). An EMPTY ARRAY is not the same thing: it
     * is the host saying „this case has no persons", and no request is issued for it.
     */
    @Input() casePersons?: CwCasePersonDto[];

    /**
     * Show everything, change nothing — passed through to `cr-customer-persons`.
     *
     * <p>⚠ It also silences `onDiscrepancyResolved` there; see the class doc. Nothing here waits for
     * that event.
     */
    @Input() readonly: boolean = false;

    /**
     * The J1 findings, emitted whenever they were recomputed — after a load and on every change of the
     * rows this component reads.
     *
     * <p>Deliberately NOT emitted per `onMatchResultChange`: that would tie a J1 statement to a J2 event.
     */
    @Output() onCheckSummary = new EventEmitter<CwCasePersonCheckSummary>();

    /**
     * One event per resolution `cr-customer-persons` actually applied — and per resolution ONCE.
     *
     * <p>The de-duplication is {@link handleDiscrepancyResolved}'s, and it is the reason applying the
     * same resolution twice is a no-op here rather than a second event a host might act on twice.
     */
    @Output() onSuggestionResolved = new EventEmitter<CrPersonResolution>();

    /** False until the first computation ran — the template's guard before it renders any finding. */
    initialized: boolean = false;

    /** A self-load is in flight. */
    busy: boolean = false;

    /** The self-load failed. Said out loud rather than shown as an empty result. */
    loadFailed: boolean = false;

    /** The rows both sections read — the `@Input` when it was bound, the self-loaded list otherwise. */
    rows: CwCasePersonDto[] = [];

    /** [J1] One finding per BOUND row, in the rows' order. */
    checkResults: CwCasePersonCheckResult[] = [];

    /** [J1] The counts behind {@link onCheckSummary}, kept for the template. */
    summary: CwCasePersonCheckSummary = CwCasePersonCheckComponent.emptySummary(null);

    /**
     * [J2] The delivery handed to `cr-customer-persons`.
     *
     * <p>⚠ Derived from {@link rows} and from NOTHING ELSE. It is never written in a match-result
     * handler — see {@link handleMatchResultChange} for why that would build a cycle this component
     * could not break.
     */
    suggestions: CrPersonSuggestion[] = [];

    /**
     * [Guard 1] The identity of the CURRENT embedded surface, as a one-element array so the template's
     * `@for … track` can tear the old instance down when it changes. See the class doc.
     */
    embeddedSurfaceKeys: number[] = [0];

    /**
     * [Guard 2] The last match snapshot, REPLACED on every event and never accumulated. Diagnostic
     * only: nothing is derived from it, no count is rendered from it, and it never becomes a delivery.
     */
    lastMatchResults: CrPersonMatchResult[] = [];

    /**
     * [Guard 2] Whether a comparison against the customer's people has actually happened.
     *
     * <p>Set idempotently to `true` — never incremented — precisely because `onMatchResultChange` fires
     * on more paths than are documented. It is worth knowing at all because that event is emitted ONLY
     * when the delivery was really held against a loaded stock; before it, „nichts gefunden" would be a
     * statement about a comparison that never ran.
     */
    assignmentComparisonRan: boolean = false;

    /**
     * [S16] What the caller found out about the customer's people — handed to
     * `checkCaseCapturedIdentities` and to nothing else.
     *
     * <p>`undefined` means the question was not asked: no customer bound, so it does not apply.
     */
    customerMatrix?: CwCasePersonCustomerMatrixOutcome;

    /** Titles and wordings, exposed for the template — see the static constants for the reasoning. */
    readonly currencyTitle = CwCasePersonCheckComponent.SECTION_CURRENCY_TITLE;
    readonly currencyQuestion = CwCasePersonCheckComponent.SECTION_CURRENCY_QUESTION;
    readonly currencyScopeNote = CwCasePersonCheckComponent.SECTION_CURRENCY_SCOPE_NOTE;
    readonly currencyDelimitation = CwCasePersonCheckComponent.SECTION_CURRENCY_DELIMITATION;
    readonly assignmentTitle = CwCasePersonCheckComponent.SECTION_ASSIGNMENT_TITLE;
    readonly assignmentQuestion = CwCasePersonCheckComponent.SECTION_ASSIGNMENT_QUESTION;
    readonly assignmentEmbedHeader = CwCasePersonCheckComponent.ASSIGNMENT_EMBED_HEADER;
    readonly loadFailedMessage = CwCasePersonCheckComponent.LOAD_FAILED_MESSAGE;
    readonly bindingColumnTitle = CwCasePersonCheckComponent.BINDING_COLUMN_TITLE;

    /** [AUFLAGE A6] Rendered verbatim, never re-worded — the same house rule as the A3 sentence. */
    readonly bindingNotVerifiableMessage = CW_CASE_PERSON_CHECK_NOT_VERIFIABLE_MESSAGE;

    /** Resolutions already forwarded, keyed by {@link resolutionKey} — the idempotency of J2's writes. */
    protected appliedResolutionKeys: Set<string> = new Set<string>();

    protected subscription: Subscription = new Subscription();

    /**
     * The CURRENT case-persons read, on its own so a superseded one can be CANCELLED.
     *
     * <p>See {@link restartPersonsRead}. Not merged into {@link subscription}: that one only ever grows
     * and is released at destroy, so a read hung in it survives every input change and its late answer
     * publishes the PREVIOUS case's rows — and, through them, the previous case's J2 delivery.
     */
    protected personsSubscription: Subscription = new Subscription();

    /** The CURRENT matrix read, on its own for the same reason — see {@link restartMatrixRead}. */
    protected matrixSubscription: Subscription = new Subscription();

    protected cwCasePersonService: MvsCrudService;

    /** Monotonic source of {@link embeddedSurfaceKeys}; a value is never reused. */
    protected surfaceKeyCounter: number = 0;

    constructor(protected coreService: MvsCoreService,
                protected personMatrixService: CrPersonMatrixService) {
    }

    ngOnInit(): void {
        this.cwCasePersonService = this.coreService.getCrudService('cw.CwCasePerson');
        this.renewEmbeddedSurface();
        this.loadCustomerMatrix();
        this.refresh();
    }

    /**
     * A changed case or customer tears the embedded surface down (guard 1); a changed case or a changed
     * row list re-derives both sections.
     *
     * <p>`customerId` alone does NOT re-read the case persons: the rows of a case do not depend on which
     * customer they are held against.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.cwCasePersonService) {
            return; // ngOnInit has not run yet; it will do the initial work.
        }

        const caseChanged = changes['caseId'] != null && !changes['caseId'].firstChange;
        const customerChanged = changes['customerId'] != null && !changes['customerId'].firstChange;
        const personsChanged = changes['casePersons'] != null && !changes['casePersons'].firstChange;

        if (caseChanged || customerChanged) {
            this.renewEmbeddedSurface();
        }

        // [S16] A new customer is a new binding question. It re-derives the findings on its own (see
        // applyCustomerMatrix) — the case's ROWS are unaffected by which customer they are held
        // against, so this deliberately does NOT re-read them.
        if (customerChanged) {
            this.loadCustomerMatrix();
        }

        if (caseChanged || personsChanged) {
            this.refresh();
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.personsSubscription.unsubscribe();
        this.matrixSubscription.unsubscribe();
    }

    /**
     * Cancels the case-persons read that is still running and opens a fresh generation for the next.
     *
     * <p>The house pattern, `CrCustomerPersonsComponent.restartLoad()`, and it exists for the same
     * reason: a read that is not cancelled still delivers. Its `next` would then write the PREVIOUS
     * case's rows over the current ones — and hand the previous case's people to the embedded J2
     * surface as a delivery, which is the assignment question asked about the wrong case.
     */
    protected restartPersonsRead(): void {
        this.personsSubscription.unsubscribe();
        this.personsSubscription = new Subscription();
    }

    /**
     * Cancels the matrix read that is still running and opens a fresh generation for the next.
     *
     * <p>⚠ On S16 a LATE answer is worse than no answer at all: the question is „hängt diese Person an
     * DIESEM Kunden?", so the previous customer's person ids, applied under the new customer, do not
     * look like a gap — they look like a FINDING. Clearing the verdict at the head of
     * {@link loadCustomerMatrix} is only half the job; without this the superseded read hangs it back
     * up the moment it returns.
     */
    protected restartMatrixRead(): void {
        this.matrixSubscription.unsubscribe();
        this.matrixSubscription = new Subscription();
    }

    /**
     * [Guard 2] `cr-customer-persons` reports what its reconciliation currently says. That is ALL this
     * does with it.
     *
     * <p><b>It must never derive {@link suggestions}.</b> The embedded surface deliberately re-runs its
     * matching after a write — a filled-in birth date can lift a person from `probable` to `unique` —
     * and emits again. A handler that answered by handing new suggestions back in would build a cycle
     * that neither side can break: the surface cannot tell a legitimate new delivery from its own last
     * emission returned to it, and this component cannot tell which emission was the last.
     *
     * <p><b>And it must never advance a counter.</b> The event fires on at least five paths, not on the
     * three that are documented; anything counted per event drifts. Hence the assignment of a snapshot
     * (replace) and an idempotent `true`, both of which are correct however often this runs.
     */
    handleMatchResultChange(results: CrPersonMatchResult[]): void {
        this.lastMatchResults = results ?? [];
        this.assignmentComparisonRan = true;
    }

    /**
     * [AUFLAGE A2] Records a resolution `cr-customer-persons` applied, and forwards it ONCE.
     *
     * <p>Applying the same resolution a second time is a NO-OP: same reference, same mode, same person,
     * same ticked row keys means the same act, and a host that reacted to it twice would do its own work
     * twice. A resolution that differs in any of those four is a different act and passes.
     *
     * <p>It touches neither {@link suggestions} nor {@link checkResults}. A write inside the embedded
     * surface changes the CUSTOMER's people, not the case's rows — so the J2 delivery stays exactly what
     * it was, the recomputation after the write terminates in the embedded surface, and nothing here
     * feeds it back.
     */
    handleDiscrepancyResolved(resolution: CrPersonResolution): void {

        if (resolution == null) {
            return;
        }

        const key = CwCasePersonCheckComponent.resolutionKey(resolution);

        if (this.appliedResolutionKeys.has(key)) {
            return;
        }

        this.appliedResolutionKeys.add(key);
        this.onSuggestionResolved.emit(resolution);
    }

    /** Retry after a failed self-load. Only reachable while the component loads its own rows. */
    handleRetry(): void {
        this.refresh();
    }

    /** [S16] Retry the customer-binding read alone — the rows are fine, only the matrix was not read. */
    handleBindingRetry(): void {
        this.loadCustomerMatrix();
    }

    /** [S16] Whether the binding question was asked at all — the gate for the extra table column. */
    get customerBindingChecked(): boolean {
        return this.customerMatrix != null;
    }

    /** [S16] Whether the matrix read FAILED — a state of its own, never shown as „nothing to report". */
    get customerBindingFailed(): boolean {
        return this.customerMatrix?.status === 'failed';
    }

    /**
     * [S16] The German short label of one row's binding verdict.
     *
     * <p>Only ever called from inside the column that {@link customerBindingChecked} gates, so an
     * absent {@link CwCasePersonCheckResult.customerBinding} genuinely means „bound" here — outside
     * that gate it would also mean „never asked", which is why the gate and this method belong
     * together.
     */
    bindingLabel(result: CwCasePersonCheckResult): string {

        switch (result.customerBinding) {
            case 'NOT_BOUND_TO_CUSTOMER':
                return CwCasePersonCheckComponent.BINDING_NOT_BOUND_LABEL;
            case 'NOT_VERIFIABLE':
                return CwCasePersonCheckComponent.BINDING_NOT_VERIFIABLE_LABEL;
            default:
                return CwCasePersonCheckComponent.BINDING_BOUND_LABEL;
        }
    }

    /** The German label of one J1 status — the template renders no status code. */
    statusLabel(result: CwCasePersonCheckResult): string {

        switch (result.status) {
            case 'MATCHING':
                return 'Stand aktuell';
            case 'DIVERGING':
                return 'Stand weicht ab';
            default:
                return 'Kein erfasster Stand';
        }
    }

    /** The captured snapshot of a J1 row as one line, or a German placeholder when there is none. */
    capturedLabel(result: CwCasePersonCheckResult): string {
        return CwCasePersonCheckComponent.nameLine(result.capturedFirstName, result.capturedLastName)
            ?? 'Kein erfasster Stand';
    }

    /** The linked person's CURRENT name as one line, or a German placeholder when it is unknown. */
    personLabel(result: CwCasePersonCheckResult): string {
        return CwCasePersonCheckComponent.nameLine(result.personFirstName, result.personLastName)
            ?? 'Name nicht ermittelbar';
    }

    /**
     * Re-derives BOTH sections from {@link rows}, or starts a self-load when the rows were not handed in.
     */
    protected refresh(): void {

        if (this.casePersons !== undefined) {
            this.busy = false;
            this.loadFailed = false;
            this.rows = this.casePersons;
            this.recompute();
            return;
        }

        this.loadCasePersons();
    }

    /**
     * Reads the case's persons with the SAME complex node the persons step uses
     * (`cw-case-object-step-persons.loadCasePersons():1238-1252`).
     *
     * <p>Both halves of that node are load-bearing here and neither may be dropped:
     * <ul>
     *   <li><b>`person` → `personDetail`</b> fills `CwCasePersonDto.personDto`, which is the „today" half
     *       of J1's pair comparison. Without it every bound row would compare its snapshot against
     *       nothing and report `DIVERGING` for the whole case.</li>
     *   <li><b>`contractPersonType`</b> keeps the self-loaded rows as rich as the ones the step hands
     *       down, so this component's two paths do not answer differently.</li>
     * </ul>
     *
     * <p>The filter attribute is `cwCase`, not `cwCase.id` — the FK is addressed by its own name.
     */
    protected loadCasePersons(): void {

        // A superseded read still delivers unless it is cancelled — see restartPersonsRead.
        this.restartPersonsRead();

        if (!this.caseId) {
            this.rows = [];
            this.recompute();
            return;
        }

        this.busy = true;
        this.loadFailed = false;

        // While a read is in flight this component knows NOTHING — so it says nothing. Keeping the
        // previous case's rows would let the embedded J2 surface mount with the PREVIOUS case's
        // delivery for as long as the read takes, which is the shape of guard 1's defect in miniature:
        // one case's people held against another case's question. `initialized` gates both sections,
        // so nothing renders from this cleared state; no summary is emitted for it either, because
        // „we do not know yet" is not a finding.
        this.clearDerivedState();

        const personNode = ObjectRequestComplexNode.createSimpleAttributeNode('person');
        personNode.addNodes(ObjectRequestComplexNode.createSimpleAttributeNode('personDetail'));

        const complexSelection = ObjectRequestComplex.build(
            true, false,
            personNode,
            ObjectRequestComplexNode.createSimpleAttributeNode('contractPersonType')
        );

        const request = ObjectRequestList.createComplexRequestList(
            false,
            complexSelection,
            [FilterCriteria.create('cwCase', FilterCriteria.cOperatorEqual, this.caseId)],
            null, null, null
        );

        this.personsSubscription.add(
            this.cwCasePersonService.list(request).subscribe({
                next: (result: any) => {
                    this.rows = result?.entries || [];
                    this.busy = false;
                    this.recompute();
                },
                error: () => {
                    this.busy = false;
                    this.loadFailed = true;
                    this.rows = [];
                    this.recompute();
                }
            })
        );
    }

    /**
     * [S16 / AUFLAGE A6] Reads the case customer's people, so J1 can say whether a bound row's person
     * actually hangs off THIS customer. Measured on dev/tenantA: 95 of 1.068 bound rows do not.
     *
     * <h3>⚠ `getMatrix`, never `setCustomerId`</h3>
     * `CrPersonMatrixService` is a root SINGLETON with a `customerId` field of its own.
     * `setCustomerId` re-points that shared field and refreshes the shared subject — i.e. it pins the
     * service to this customer for the rest of the browser session and pushes this case's customer at
     * every other consumer of `getMatrixSubject()`. `getMatrix(customerId)` is a plain HTTP read that
     * touches none of that state, which is why it is the only member of this service this component
     * is allowed to call. This is the same class of defect `[allowCreate]="false"` guards against on
     * the J2 side.
     *
     * <h3>A failed read is REPORTED, not swallowed (AUFLAGE A6)</h3>
     * The error branch hands `{status: 'failed'}` down, which marks every bound row `NOT_VERIFIABLE`.
     * A malformed body is treated the same way: without a `columns` array we did not get a matrix, and
     * inferring an EMPTY one would accuse every bound row of the case of being customer-foreign on the
     * strength of an infrastructure hiccup.
     */
    protected loadCustomerMatrix(): void {

        // BOTH halves are needed, and the first one alone is the defect this pair closes:
        //  1. cancel the superseded read, so its late answer can never hang the old verdict back up;
        //  2. clear the verdict, so nothing stale is on screen while the new read is in flight.
        this.restartMatrixRead();
        this.applyCustomerMatrix(undefined);

        if (this.customerId == null) {
            return;
        }

        this.matrixSubscription.add(
            this.personMatrixService.getMatrix(this.customerId).subscribe({
                next: (matrix: CustomerPersonMatrixDto) =>
                    this.applyCustomerMatrix(CwCasePersonCheckComponent.toMatrixOutcome(matrix)),
                error: () => this.applyCustomerMatrix({status: 'failed'}),
            })
        );
    }

    /**
     * [S16] Stores a binding answer and re-derives the findings with it.
     *
     * <p>Recomputes only once the rows are known ({@link initialized}). Before that there is nothing to
     * annotate, and recomputing would publish an empty summary for a case whose rows are still being
     * read — the row path picks the stored answer up on its own.
     */
    protected applyCustomerMatrix(outcome: CwCasePersonCustomerMatrixOutcome | undefined): void {

        this.customerMatrix = outcome;

        if (this.initialized) {
            this.recompute();
        }
    }

    /**
     * The person ids of a matrix, as the S16 check needs them — or `failed` when the body is not a
     * matrix at all. See {@link loadCustomerMatrix} for why a missing `columns` array is a FAILURE and
     * not an empty answer.
     */
    protected static toMatrixOutcome(matrix: CustomerPersonMatrixDto): CwCasePersonCustomerMatrixOutcome {

        if (!matrix || !Array.isArray(matrix.columns)) {
            return {status: 'failed'};
        }

        const personIds = new Set<number>();

        matrix.columns.forEach(column => {
            if (typeof column?.personId === 'number') {
                personIds.add(column.personId);
            }
        });

        return {status: 'loaded', personIds};
    }

    /**
     * Both sections, from the rows and from nothing else — the single place either is derived.
     *
     * <p>The two row sets are DISJOINT by construction: `checkCaseCapturedIdentities` selects
     * `typeof personDtoId === 'number'`, `unlinkedCasePersonSuggestions` its negation. No row can appear
     * in both sections, which is the structural half of AUFLAGE A3 (the labelling is the visible half).
     */
    protected recompute(): void {

        // [S16] The binding answer rides along here and nowhere else — it annotates the J1 findings and
        // never touches the J2 delivery, which is a question about names, not about bindings.
        this.checkResults = checkCaseCapturedIdentities(this.rows, this.customerMatrix);
        this.suggestions = unlinkedCasePersonSuggestions(this.rows);
        this.summary = CwCasePersonCheckComponent.summaryOf(
            this.caseId ?? null, this.checkResults, this.customerMatrix);
        this.initialized = true;

        this.onCheckSummary.emit(this.summary);
    }

    /**
     * Everything derived from the rows, back to „not known yet" — used while a read is in flight.
     *
     * <p>It deliberately does NOT emit: {@link onCheckSummary} reports FINDINGS, and an emptied state
     * is the absence of findings, not the finding that there are none. A host that saw a zeroed summary
     * on every reload could not tell „nichts gefunden" from „noch nicht geprüft".
     */
    protected clearDerivedState(): void {

        this.initialized = false;
        this.rows = [];
        this.checkResults = [];
        this.suggestions = [];
        this.summary = CwCasePersonCheckComponent.emptySummary(this.caseId ?? null);
    }

    /**
     * [Guard 1] Tears the embedded surface down and marks a NEW one, and forgets everything that
     * belonged to the old customer: the applied resolutions and the match snapshot are statements about
     * a stock this component is no longer looking at.
     */
    protected renewEmbeddedSurface(): void {

        this.surfaceKeyCounter += 1;
        this.embeddedSurfaceKeys = [this.surfaceKeyCounter];
        this.appliedResolutionKeys = new Set<string>();
        this.lastMatchResults = [];
        this.assignmentComparisonRan = false;
    }

    /**
     * The identity of one applied resolution — reference, mode, bound person and the ticked row keys.
     *
     * <p>All four belong in it. Two resolutions for the same delivered row that write DIFFERENT fields
     * are two different acts and both must pass; only the literally identical act is the repetition
     * AUFLAGE A2 asks to swallow. The keys are sorted so the order the reviewer ticked them in does not
     * make the same act look like a new one.
     */
    protected static resolutionKey(resolution: CrPersonResolution): string {

        const rowKeys = [...(resolution.appliedRowKeys ?? [])].sort().join(',');

        return [resolution.reference, resolution.mode, resolution.personId ?? '', rowKeys].join('|');
    }

    /**
     * The counts of a finding list — one pass, no derived state anywhere else.
     *
     * <p>[S16] `notBoundToCustomer` is counted alongside the three NAME counts and is deliberately not
     * a subset of any of them: the two dimensions are orthogonal, so a row can be counted in both.
     */
    protected static summaryOf(caseId: number | null,
                               results: CwCasePersonCheckResult[],
                               customerMatrix?: CwCasePersonCustomerMatrixOutcome,
    ): CwCasePersonCheckSummary {

        const summary = CwCasePersonCheckComponent.emptySummary(caseId);

        summary.checked = results.length;
        summary.results = results;
        summary.customerBindingCheck = customerMatrix == null ? 'skipped' : customerMatrix.status;

        results.forEach(result => {
            if (result.status === 'MATCHING') {
                summary.matching += 1;
            } else if (result.status === 'DIVERGING') {
                summary.diverging += 1;
            } else {
                summary.withoutCapturedIdentity += 1;
            }

            if (result.customerBinding === 'NOT_BOUND_TO_CUSTOMER') {
                summary.notBoundToCustomer += 1;
            }
        });

        return summary;
    }

    /** A zeroed summary — the state before anything was computed. */
    protected static emptySummary(caseId: number | null): CwCasePersonCheckSummary {
        return {
            caseId,
            checked: 0,
            matching: 0,
            diverging: 0,
            withoutCapturedIdentity: 0,
            notBoundToCustomer: 0,
            customerBindingCheck: 'skipped',
            results: [],
        };
    }

    /** „Vorname Nachname", each half skipped when blank; `null` when both are. */
    protected static nameLine(firstName?: string, lastName?: string): string | null {

        const line = [firstName, lastName]
            .map(part => (part ?? '').trim())
            .filter(part => part.length > 0)
            .join(' ');

        return line.length > 0 ? line : null;
    }
}
